import { formatFieldType } from '@modules/formatters';
import { useCallback } from 'react';
import type { CardFields, CardPresetFields, CardPresetUrl, CardUrl, FieldTypes } from 'types';

import styles from './AddFieldSection.module.scss';
import FieldButton, { FieldButtonIcon } from './FieldButton';
import FieldTile from './FieldTile';
import { FIELD_SECTIONS, type FieldDefinition } from './field-sections';

type AddCardFieldSectionProps = {
  fields: CardFields | CardPresetFields;
  onAdd: (type: FieldTypes, subType: CardUrl['type'] | CardPresetUrl['type'] | undefined) => void;
  canLock?: boolean;
  lockedFieldTypes?: string[];
  onToggleFieldTypeLock?: (id: string, toggleState?: boolean) => void;
  tileVariant?: 'classic' | 'compact';
  /** Field types rendered elsewhere on the page, so their tile would be redundant. */
  hiddenFieldTypes?: FieldTypes[];
};

function isFieldDisabled(fields: CardFields | CardPresetFields, field: FieldDefinition): boolean {
  if (field.type === 'name') {
    return 'firstName' in fields && !!fields.firstName;
  }
  if (field.type === 'job_title') return !!fields.jobTitle;
  if (field.type === 'department') return !!fields.department;
  if (field.type === 'org_name') return !!fields.orgName;
  if (field.type === 'headline') return !!fields.headline;
  if (field.type === 'accreditations') {
    return !!fields.accreditations && fields.accreditations.filter((v) => v).length > 0;
  }
  return false;
}

function shouldRenderField(fields: CardFields | CardPresetFields, field: FieldDefinition): boolean {
  // 'name' is only available on CardFields (which has firstName/lastName), not CardPresetFields.
  if (field.type === 'name') return 'firstName' in fields;
  return true;
}

const AddCardFieldSection: React.FC<React.PropsWithChildren<AddCardFieldSectionProps>> = ({
  fields,
  onAdd,
  canLock = false,
  lockedFieldTypes = [],
  onToggleFieldTypeLock,
  tileVariant = 'classic',
  hiddenFieldTypes = [],
}) => {
  const handleLock = useCallback(
    (type: string) => {
      onToggleFieldTypeLock?.(type);
    },
    [onToggleFieldTypeLock],
  );

  const TileComponent = tileVariant === 'compact' ? FieldTile : FieldButton;

  return (
    <div className={styles['add-field-section']} data-variant={tileVariant}>
      {FIELD_SECTIONS.map((section) => (
        <div key={section.heading} className={styles['add-field-subsection']}>
          <h4 className={styles['add-field-heading']}>{section.heading}</h4>
          <ul className={styles['field-list']}>
            {section.fields
              .filter(
                (field) =>
                  shouldRenderField(fields, field) && !hiddenFieldTypes.includes(field.type),
              )
              .map((field) => {
                const lockKey = field.subType ?? field.type;
                const label =
                  field.type === 'url'
                    ? formatFieldType('url', field.subType)
                    : formatFieldType(field.type);

                const commonProps = {
                  onClick: onAdd,
                  canLock,
                  onLock: handleLock,
                  locked: lockedFieldTypes.includes(lockKey),
                  disabled: isFieldDisabled(fields, field),
                  label,
                };

                if (field.type === 'url') {
                  return (
                    <li key={`url-${field.subType}`}>
                      <TileComponent
                        {...commonProps}
                        type="url"
                        subType={field.subType}
                        icon={<FieldButtonIcon type="url" subType={field.subType} />}
                      />
                    </li>
                  );
                }

                return (
                  <li key={field.type}>
                    <TileComponent
                      {...commonProps}
                      type={field.type}
                      icon={<FieldButtonIcon type={field.type} />}
                    />
                  </li>
                );
              })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default AddCardFieldSection;
